import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  checkboxClass,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getStDischargeNonVisitForm,
  updateStDischargeNonVisitForm,
} from "../../../../../services/patientService";
import {
  AlertCircleIcon,
  CommentsIcon,
  FileCheckIcon,
  GraduationCapIcon,
  HeartPulseIcon,
  HomeIcon,
  LogOutIcon,
  MedicalNotesIcon,
  PhoneIcon,
  ShieldIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  ST_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import PainScalePills from "../ptNotes/PainScalePills";
import {
  ASSIST_LEVEL_OPTIONS,
  CAREGIVER_INVOLVEMENT_OPTIONS,
  CMS_CHECKLIST_OPTIONS,
  DISCHARGE_TYPE_OPTIONS,
  EDUCATION_PROVIDED_OPTIONS,
  FOLLOW_UP_OPTIONS,
  FUNCTIONAL_DOMAIN_ROWS,
  GOAL_OUTCOME_STATUS_OPTIONS,
  HOME_ENVIRONMENT_OPTIONS,
  HOMEBOUND_REASON_OPTIONS,
  MEDICAL_NECESSITY_OPTIONS,
  OVERALL_OUTCOME_OPTIONS,
  PHYSICIAN_NOTIFY_METHOD_OPTIONS,
  SAFETY_CONCERN_OPTIONS,
  ST_DNV_ARRAY_KEYS,
  ST_SERVICE_REASON_OPTIONS,
  YES_NO_NA_OPTIONS,
  YES_NO_OPTIONS,
  emptyStDischargeNonVisitPayload,
  mergeFunctionalStatus,
  mergeGoalOutcomes,
  toggleInList,
  today,
} from "./stDischargeNonVisitContent";
import {
  assertPtVisitMetaForFlag,
  buildPtVisitMetaProps,
  pickPtVisitMeta,
} from "../ptNotes/ptVisitCommon";
import StNotesCommonHeader from "./StNotesCommonHeader";
import {
  normalizeStCommonHeaderState,
  resolveStNotesCommonHeader,
  withNormalizedStCommonHeader,
} from "./stNotesCommonHeaderUtils";

const DATE_MAX = new Date().toISOString().slice(0, 10);

const tableHeadClass =
  "bg-indigo-50/70 text-[9px] font-semibold uppercase tracking-wide text-indigo-700";

const normalizePainLevel = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 && n <= 10 ? n : null;
};

const emptyValues = () => ({
  ...emptyStDischargeNonVisitPayload(),
  dischargeDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const PillToggle = ({ name, options, value, canEdit, onChange }) => (
  <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={name}>
    {options.map((opt) => {
      const selected = value === opt;
      return (
        <button
          key={opt}
          type="button"
          disabled={!canEdit}
          aria-pressed={selected}
          onClick={() => onChange(selected ? "" : opt)}
          className={`h-7 px-2.5 rounded-md border text-[11px] font-semibold whitespace-nowrap transition-colors ${
            selected
              ? "border-indigo-600 bg-indigo-600 text-white"
              : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
          } ${!canEdit ? "cursor-default opacity-80" : "cursor-pointer"}`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

PillToggle.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const StDischargeNonVisitForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyStDischargeNonVisitPayload();
      const loadedPayload =
        data.payload &&
        typeof data.payload === "object" &&
        !Array.isArray(data.payload)
          ? data.payload
          : {};

      const arrays = {};
      ST_DNV_ARRAY_KEYS.forEach((key) => {
        arrays[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key]
          : base[key];
      });

      setValues({
        ...base,
        ...loadedPayload,
        ...arrays,
        ...pickPtVisitMeta(loadedPayload, data),
        dischargeDate:
          data.dischargeDate || loadedPayload.dischargeDate || today(),
        commonHeader: normalizeStCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        goalOutcomes: mergeGoalOutcomes(loadedPayload.goalOutcomes),
        functionalStatus: mergeFunctionalStatus(loadedPayload.functionalStatus),
        painLevel: normalizePainLevel(loadedPayload.painLevel),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      });
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveStNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Speech Therapy Discharge (Non-Visit)",
            formType:
              clinicalForm.formType ||
              draft?.formType ||
              "st_discharge_non_visit",
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            dischargeDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...emptyStDischargeNonVisitPayload(),
              commonHeader,
            },
          });
          return;
        }

        const res = await getStDischargeNonVisitForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveStNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...existingPayload, commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleArray = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const updateGoal = (id, patch) => {
    setValues((prev) => ({
      ...prev,
      goalOutcomes: (prev.goalOutcomes || []).map((row) =>
        row.id === id ? { ...row, ...patch } : row,
      ),
    }));
  };

  const setAssistLevel = (domain, side, level) => {
    setValues((prev) => {
      const current = prev.functionalStatus?.[domain]?.[side];
      return {
        ...prev,
        functionalStatus: {
          ...(prev.functionalStatus || {}),
          [domain]: {
            ...(prev.functionalStatus?.[domain] || { soc: "", discharge: "" }),
            [side]: current === level ? "" : level,
          },
        },
      };
    });
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        ST_FORM_EDITOR_ROLES,
        "View only — only ST can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const { caregiverSlot, filledByUserId, assertCaregiverForCreate } =
    useFormCaregiverPicker({
      patientId,
      formType:
        form?.formType ||
        draft?.formType ||
        clinicalForm?.formType ||
        "st_discharge_non_visit",
      role,
      canEdit,
      preferredStaffId: draft?.visit?.staffId ?? null,
      currentFilledByUserId: form?.filledBy || clinicalForm?.filledBy || null,
    });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      dischargeDate,
      ...stFields
    } = values;

    const nestedPayload = withNormalizedStCommonHeader({
      ...stFields,
      dischargeDate: dischargeDate ? formatDateForInput(dischargeDate) : "",
      physicianNotifiedDate: values.physicianNotifiedDate
        ? formatDateForInput(values.physicianNotifiedDate)
        : "",
      painLevel: normalizePainLevel(values.painLevel),
      goalOutcomes: mergeGoalOutcomes(values.goalOutcomes).map((row) => ({
        ...row,
        dateMet: row.dateMet ? formatDateForInput(row.dateMet) : "",
      })),
      functionalStatus: mergeFunctionalStatus(values.functionalStatus),
    });

    const payload = {
      flag,
      dischargeDate: dischargeDate ? formatDateForInput(dischargeDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    const visitMetaError = assertPtVisitMetaForFlag(values, flag, {
      requireTimes: false,
    });
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateStDischargeNonVisitForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.dischargeDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showTypeOther = values.dischargeType === "Other (Specify)";
  const showStOrderedExplain = values.stOrderedForPrimary === "No";
  const functional = values.functionalStatus || {};

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            {form.formName || "Speech Therapy Discharge (Non-Visit)"}
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Home Health Services | CMS Compliant Documentation
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Discharge Date"
          dateValue={displayDateUS(values.dischargeDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400 leading-none">
                  Discharge Date
                </div>
                <InputDatePicker
                  name="dischargeDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.dischargeDate) || ""}
                  onChange={(event) =>
                    setValue("dischargeDate", event.target.value)
                  }
                  max={DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildPtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <StNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <Section icon={LogOutIcon} title="7. Discharge Information (Non-Visit)">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <Field label="Discipline">
              <div className={`${fieldClass} flex items-center bg-slate-50`}>
                {values.discipline || "Speech-Language Pathology"}
              </div>
            </Field>
            <Field label="Discharge Date (Non-Visit)">
              {printMode ? (
                <div className={`${fieldClass} flex items-center`}>
                  {displayDateUS(values.dischargeDate) || "—"}
                </div>
              ) : (
                <InputDatePicker
                  name="sectionDischargeDate"
                  formatSeparator="/"
                  allowManualEntry
                  disabled={!canEdit}
                  value={formatDateForInput(values.dischargeDate) || ""}
                  onChange={(event) =>
                    setValue("dischargeDate", event.target.value)
                  }
                  max={DATE_MAX}
                  className="w-full"
                  inputClassName={`${fieldClass} bg-white`}
                />
              )}
            </Field>
          </div>
          <div className="mt-2.5">
            <SubHeading>Discharge Type (Check One)</SubHeading>
            <ExclusiveChoiceGrid
              name="dischargeType"
              options={DISCHARGE_TYPE_OPTIONS}
              value={values.dischargeType || ""}
              canEdit={canEdit}
              columns={2}
              onChange={(opt) => {
                setValues((prev) => ({
                  ...prev,
                  dischargeType: opt,
                  dischargeTypeOther:
                    opt === "Other (Specify)" ? prev.dischargeTypeOther : "",
                }));
              }}
            />
            {showTypeOther ? (
              <input
                type="text"
                className={`${fieldClass} mt-1.5`}
                value={values.dischargeTypeOther || ""}
                disabled={!canEdit}
                placeholder="Specify other discharge type…"
                onChange={(e) => setValue("dischargeTypeOther", e.target.value)}
              />
            ) : null}
          </div>
          <div className="mt-2">
            <Field label="If Other, Please Explain">
              <textarea
                rows={2}
                className={`${textareaClass} min-h-[3.5rem]`}
                value={values.dischargeTypeExplain || ""}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("dischargeTypeExplain", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 flex gap-2 items-start">
          <AlertCircleIcon className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
          <p>
            A face-to-face visit is <strong>not</strong> required for a non-visit
            discharge; the clinical record must clearly document the reason for
            discharge without a visit.
          </p>
        </div>

        <Section icon={MedicalNotesIcon} title="8. Primary Reason for Home Health Services">
          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,0.28fr)_minmax(0,0.72fr)] gap-3 items-end">
            <Field label="Primary Diagnosis (ICD-10-CM)">
              <input
                type="text"
                className={fieldClass}
                value={values.primaryHhDiagnosisCode || ""}
                disabled={!canEdit}
                placeholder="e.g., I69.320"
                onChange={(e) =>
                  setValue("primaryHhDiagnosisCode", e.target.value)
                }
              />
            </Field>
            <Field label="Description">
              <input
                type="text"
                className={fieldClass}
                value={values.primaryHhDescription || ""}
                disabled={!canEdit}
                placeholder="Diagnosis description…"
                onChange={(e) =>
                  setValue("primaryHhDescription", e.target.value)
                }
              />
            </Field>
          </div>
          <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[12px] text-slate-800">
              Was ST ordered to address the primary reason?
            </p>
            <PillToggle
              name="stOrderedForPrimary"
              options={YES_NO_OPTIONS}
              value={values.stOrderedForPrimary || ""}
              canEdit={canEdit}
              onChange={(v) => setValue("stOrderedForPrimary", v)}
            />
          </div>
          {showStOrderedExplain ? (
            <div className="mt-2">
              <Field label="If No, Explain">
                <textarea
                  rows={2}
                  className={`${textareaClass} min-h-[3.5rem]`}
                  value={values.stOrderedExplain || ""}
                  disabled={!canEdit}
                  onChange={(e) => setValue("stOrderedExplain", e.target.value)}
                />
              </Field>
            </div>
          ) : null}
        </Section>

        <Section icon={CommentsIcon} title="9. Reason for ST Services">
          <CheckboxGrid
            options={ST_SERVICE_REASON_OPTIONS}
            values={values.stServiceReasons}
            canEdit={canEdit}
            onToggle={(opt) => toggleArray("stServiceReasons", opt)}
            columns={2}
          />
          <div className="mt-2">
            <Field label="Other Reason Details">
              <textarea
                rows={2}
                className={`${textareaClass} min-h-[3.5rem]`}
                value={values.stServiceOtherDetails || ""}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("stServiceOtherDetails", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        <Section icon={TargetIcon} title="10. Goals and Outcomes">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full min-w-[680px] text-left">
              <thead>
                <tr className={tableHeadClass}>
                  <th className="px-2 py-1.5 w-10">#</th>
                  <th className="px-2 py-1.5">Goal Description</th>
                  <th className="px-2 py-1.5 w-[22%]">Outcome</th>
                  <th className="px-2 py-1.5 w-[18%]">Date Met</th>
                </tr>
              </thead>
              <tbody>
                {(values.goalOutcomes || []).map((row, idx) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 align-middle text-center">
                      <input
                        type="checkbox"
                        className={checkboxClass}
                        checked={Boolean(row.included)}
                        disabled={!canEdit}
                        onChange={(e) =>
                          updateGoal(row.id, { included: e.target.checked })
                        }
                      />
                      <span className="sr-only">Goal {idx + 1}</span>
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.description || ""}
                        disabled={!canEdit}
                        placeholder={`Goal ${idx + 1} description…`}
                        onChange={(e) =>
                          updateGoal(row.id, { description: e.target.value })
                        }
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <select
                        className={fieldClass}
                        value={row.outcome || ""}
                        disabled={!canEdit}
                        onChange={(e) =>
                          updateGoal(row.id, { outcome: e.target.value })
                        }
                      >
                        <option value="">Select…</option>
                        {GOAL_OUTCOME_STATUS_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      {printMode ? (
                        <div className={`${fieldClass} flex items-center`}>
                          {displayDateUS(row.dateMet) || "—"}
                        </div>
                      ) : (
                        <InputDatePicker
                          name={`goalDate-${row.id}`}
                          formatSeparator="/"
                          allowManualEntry
                          disabled={!canEdit}
                          value={formatDateForInput(row.dateMet) || ""}
                          onChange={(event) =>
                            updateGoal(row.id, { dateMet: event.target.value })
                          }
                          max={DATE_MAX}
                          className="w-full"
                          inputClassName={`${fieldClass} bg-white`}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2.5">
            <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
              Overall Outcome
            </p>
            <PillToggle
              name="overallOutcome"
              options={OVERALL_OUTCOME_OPTIONS}
              value={values.overallOutcome || ""}
              canEdit={canEdit}
              onChange={(v) => setValue("overallOutcome", v)}
            />
          </div>
          <div className="mt-2">
            <Field label="Outcome Rationale / Summary">
              <textarea
                rows={3}
                className={`${textareaClass} min-h-[4.5rem]`}
                value={values.outcomeRationale || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("outcomeRationale", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section
          icon={HeartPulseIcon}
          title="11. Patient's Status at Discharge (Based on Most Recent Assessment)"
        >
          <SubHeading>Functional Communication Status</SubHeading>
          <div className="overflow-x-auto rounded-md border border-slate-200 mb-2.5">
            <table className="w-full min-w-[760px] text-left text-[11px]">
              <thead>
                <tr className={tableHeadClass}>
                  <th className="px-2 py-1.5" rowSpan={2}>
                    Domain
                  </th>
                  <th
                    className="px-2 py-1 text-center border-l border-indigo-100"
                    colSpan={5}
                  >
                    At SOC
                  </th>
                  <th
                    className="px-2 py-1 text-center border-l border-indigo-100"
                    colSpan={5}
                  >
                    At Discharge
                  </th>
                </tr>
                <tr className={tableHeadClass}>
                  {["soc", "discharge"].map((side) =>
                    ASSIST_LEVEL_OPTIONS.map((lvl) => (
                      <th
                        key={`${side}-${lvl.key}`}
                        className={`px-1 py-1 text-center w-9 ${
                          lvl.key === "Ind" ? "border-l border-indigo-100" : ""
                        }`}
                      >
                        {lvl.label}
                      </th>
                    )),
                  )}
                </tr>
              </thead>
              <tbody>
                {FUNCTIONAL_DOMAIN_ROWS.map((domain) => (
                  <tr key={domain} className="border-t border-slate-100">
                    <td className="px-2 py-1 font-medium text-slate-700 whitespace-nowrap">
                      {domain}
                    </td>
                    {["soc", "discharge"].map((side) =>
                      ASSIST_LEVEL_OPTIONS.map((lvl) => (
                        <td
                          key={`${domain}-${side}-${lvl.key}`}
                          className={`px-1 py-1 text-center ${
                            lvl.key === "Ind" ? "border-l border-slate-100" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name={`fs-${domain}-${side}`}
                            className={checkboxClass}
                            checked={functional[domain]?.[side] === lvl.key}
                            disabled={!canEdit}
                            onChange={() =>
                              setAssistLevel(domain, side, lvl.key)
                            }
                            onClick={() => {
                              if (
                                canEdit &&
                                functional[domain]?.[side] === lvl.key
                              ) {
                                setAssistLevel(domain, side, lvl.key);
                              }
                            }}
                          />
                        </td>
                      )),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <SubHeading>Other Clinical Measures</SubHeading>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 items-end">
            {[
              ["swallowingSafety", "Swallowing Safety"],
              ["dietConsistency", "Diet / Consistency"],
              ["communicationEffectiveness", "Communication Effectiveness"],
              ["participationInAdls", "Participation in ADLs"],
              ["caregiverCapacity", "Caregiver Capacity"],
            ].map(([key, label]) => (
              <Field key={key} label={label}>
                <input
                  type="text"
                  className={fieldClass}
                  value={values[key] || ""}
                  disabled={!canEdit}
                  onChange={(e) => setValue(key, e.target.value)}
                />
              </Field>
            ))}
          </div>

          <div className="mt-2.5 max-w-2xl">
            <PainScalePills
              label="Pain Score (0–10)"
              name="painLevel"
              value={values.painLevel}
              canEdit={canEdit}
              onChange={(v) => setValue("painLevel", v)}
            />
          </div>

          <div className="mt-2">
            <Field label="Comments">
              <textarea
                rows={3}
                className={`${textareaClass} min-h-[4.5rem]`}
                value={values.statusComments || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("statusComments", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={HomeIcon} title="12. Homebound Status at Discharge">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <p className="text-[12px] text-slate-800">Remains homebound?</p>
            <PillToggle
              name="remainsHomebound"
              options={YES_NO_NA_OPTIONS}
              value={values.remainsHomebound || ""}
              canEdit={canEdit}
              onChange={(v) => setValue("remainsHomebound", v)}
            />
          </div>
          <CheckboxGrid
            options={HOMEBOUND_REASON_OPTIONS}
            values={values.homeboundReasons}
            canEdit={canEdit}
            onToggle={(opt) => toggleArray("homeboundReasons", opt)}
            columns={1}
          />
          <div className="mt-2">
            <Field label="Comments">
              <textarea
                rows={2}
                className={`${textareaClass} min-h-[3.5rem]`}
                value={values.homeboundComments || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("homeboundComments", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={ShieldIcon} title="13. Safety and Environment">
          <SubHeading>Home Environment</SubHeading>
          <CheckboxGrid
            options={HOME_ENVIRONMENT_OPTIONS}
            values={values.homeEnvironment}
            canEdit={canEdit}
            onToggle={(opt) => toggleArray("homeEnvironment", opt)}
            columns={3}
          />
          <div className="mt-2.5">
            <SubHeading>Caregiver Involvement</SubHeading>
            <CheckboxGrid
              options={CAREGIVER_INVOLVEMENT_OPTIONS}
              values={values.caregiverInvolvement}
              canEdit={canEdit}
              onToggle={(opt) => toggleArray("caregiverInvolvement", opt)}
              columns={2}
            />
          </div>
          <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[12px] text-slate-800">
              Caregiver able to manage needs?
            </p>
            <PillToggle
              name="caregiverManageNeeds"
              options={YES_NO_NA_OPTIONS}
              value={values.caregiverManageNeeds || ""}
              canEdit={canEdit}
              onChange={(v) => setValue("caregiverManageNeeds", v)}
            />
          </div>
          <div className="mt-2.5">
            <SubHeading>Safety Concerns Addressed</SubHeading>
            <CheckboxGrid
              options={SAFETY_CONCERN_OPTIONS}
              values={values.safetyConcerns}
              canEdit={canEdit}
              onToggle={(opt) => toggleArray("safetyConcerns", opt)}
              columns={3}
            />
          </div>
          <div className="mt-2">
            <Field label="Comments">
              <textarea
                rows={2}
                className={`${textareaClass} min-h-[3.5rem]`}
                value={values.safetyComments || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("safetyComments", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={GraduationCapIcon} title="14. Discharge Planning">
          <SubHeading>Education Provided</SubHeading>
          <CheckboxGrid
            options={EDUCATION_PROVIDED_OPTIONS}
            values={values.educationProvided}
            canEdit={canEdit}
            onToggle={(opt) => toggleArray("educationProvided", opt)}
            columns={3}
          />
          <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[12px] text-slate-800">
              Understanding demonstrated?
            </p>
            <PillToggle
              name="understandingDemonstrated"
              options={YES_NO_NA_OPTIONS}
              value={values.understandingDemonstrated || ""}
              canEdit={canEdit}
              onChange={(v) => setValue("understandingDemonstrated", v)}
            />
          </div>
          <div className="mt-2.5">
            <SubHeading>Follow-Up Recommended</SubHeading>
            <CheckboxGrid
              options={FOLLOW_UP_OPTIONS}
              values={values.followUpRecommended}
              canEdit={canEdit}
              onToggle={(opt) => toggleArray("followUpRecommended", opt)}
              columns={2}
            />
          </div>
          <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[12px] text-slate-800">
              Discharge instructions provided?
            </p>
            <PillToggle
              name="instructionsProvided"
              options={YES_NO_OPTIONS}
              value={values.instructionsProvided || ""}
              canEdit={canEdit}
              onChange={(v) => setValue("instructionsProvided", v)}
            />
          </div>
          <div className="mt-2">
            <Field label="Comments">
              <textarea
                rows={2}
                className={`${textareaClass} min-h-[3.5rem]`}
                value={values.planningComments || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("planningComments", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={FileCheckIcon} title="15. Medical Necessity / Discharge Summary">
          <CheckboxGrid
            options={MEDICAL_NECESSITY_OPTIONS}
            values={values.medicalNecessityReasons}
            canEdit={canEdit}
            onToggle={(opt) => toggleArray("medicalNecessityReasons", opt)}
            columns={2}
          />
          <div className="mt-2">
            <Field label="Summary / Additional Information">
              <textarea
                rows={5}
                className={`${textareaClass} min-h-[6.5rem]`}
                value={values.dischargeSummaryNarrative || ""}
                disabled={!canEdit}
                placeholder="Document why skilled ST is no longer medically necessary…"
                onChange={(e) =>
                  setValue("dischargeSummaryNarrative", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        <Section icon={PhoneIcon} title="16. Physician Notification">
          <div className="grid grid-cols-1 sm:grid-cols-[auto_minmax(0,1fr)] gap-3 items-end">
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Notified
              </p>
              <PillToggle
                name="physicianNotified"
                options={YES_NO_OPTIONS}
                value={values.physicianNotified || ""}
                canEdit={canEdit}
                onChange={(v) => setValue("physicianNotified", v)}
              />
            </div>
            <Field label="Date Notified">
              {printMode ? (
                <div className={`${fieldClass} flex items-center`}>
                  {displayDateUS(values.physicianNotifiedDate) || "—"}
                </div>
              ) : (
                <InputDatePicker
                  name="physicianNotifiedDate"
                  formatSeparator="/"
                  allowManualEntry
                  disabled={!canEdit}
                  value={formatDateForInput(values.physicianNotifiedDate) || ""}
                  onChange={(event) =>
                    setValue("physicianNotifiedDate", event.target.value)
                  }
                  max={DATE_MAX}
                  className="w-full"
                  inputClassName={`${fieldClass} bg-white`}
                />
              )}
            </Field>
          </div>
          <div className="mt-2.5">
            <SubHeading>Method</SubHeading>
            <CheckboxGrid
              options={PHYSICIAN_NOTIFY_METHOD_OPTIONS}
              values={values.physicianNotifyMethods}
              canEdit={canEdit}
              onToggle={(opt) => toggleArray("physicianNotifyMethods", opt)}
              columns={4}
            />
          </div>
          <div className="mt-2">
            <Field label="Comments">
              <textarea
                rows={2}
                className={`${textareaClass} min-h-[3.5rem]`}
                value={values.physicianNotifyComments || ""}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("physicianNotifyComments", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        <Section
          icon={FileCheckIcon}
          title="17. CMS / Medicare Documentation Compliance Checklist"
        >
          <CheckboxGrid
            options={CMS_CHECKLIST_OPTIONS}
            values={values.cmsChecklist}
            canEdit={canEdit}
            onToggle={(opt) => toggleArray("cmsChecklist", opt)}
            columns={3}
          />
        </Section>

        <FormSignatureSections
          {...signatureProps}
          canEdit={canEdit}
          canSign={canSign}
          printMode={printMode}
          values={values}
          onPatientSign={signatures.handlePatientSign}
          onClinicianSign={handleClinicianSign}
          onChange={setValues}
          clinicianLabel="Clinician Digital Signature"
          clinicianSectionTitle="Clinician Digital Signature"
        />

        {isReviewer ? (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        ) : values.reviewComments ? (
          <OfficeCommentsNote comments={values.reviewComments} />
        ) : null}

        {!printMode &&
          (permissions.canSendToOffice ||
            permissions.canToBeSign ||
            permissions.canReview ||
            permissions.canEdit) && (
            <FormActionBar
              permissions={permissions}
              status={form.status}
              canEdit={canEdit}
              saving={saving}
              onSubmit={submit}
            />
          )}
      </div>
    </div>
  );
};

StDischargeNonVisitForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default StDischargeNonVisitForm;
